import { Dialog, FullscreenDialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { 
  MapPin, 
  DollarSign, 
  Clock, 
  Building, 
  Building2,
  Calendar,
  Briefcase,
  ExternalLink,
  Globe,
  Heart,
  Share2,
  X,
  Send,
  Eye
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useSecureReferralToken } from "@/hooks/useSecureReferralToken";
import { useAccessFlags, useUser } from "@/hooks/auth";
import { ReferralExplanationDialog } from "./ReferralExplanationDialog";
import { ReferralBountyDialog } from "./ReferralBountyDialog";
import { useState } from "react";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { supabase } from "@/integrations/supabase/client";
import { BountyWorkflowSummary } from "@/types/bounty-workflow";
import { MarkdownMessage } from "@/components/ui/markdown-message";
import { DisplaySmall, HeadingMedium, Caption, BodyMedium } from "@/components/ui/typography";
import { formatSalary } from "@/utils/formatters";
import { useTranslation } from "@/hooks/useTranslation";

interface Job {
  id: string;
  short_code?: string;
  title: string;
  description: string;
  location?: string;
  salary_min?: number;
  salary_max?: number;
  currency?: string;
  salary_period?: string;
  employment_type?: string;
  created_at: string;
  user_id?: string;
  company_id?: string;
  status?: string;
  has_ai_interviewer?: boolean;
  company_profiles?: {
    name?: string;
    company_name?: string;
    logo_url?: string;
    company_logo_url?: string;
    company_identifier?: string;
    short_code?: string;
    description?: string;
    industry?: string;
    website?: string;
  };
  company?: {
    name: string;
    logo_url?: string;
    company_identifier?: string;
    short_code?: string;
  };
}

interface JobDetailDialogProps {
  job: Job | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplicationSubmitted?: () => void;
  onApplyClick?: (job: Job) => void;
  onSaveJob?: (jobId: string) => void;
  applicationSubmitted?: boolean;
  isSaved?: boolean;
}

export function JobDetailDialog({ 
  job, 
  open, 
  onOpenChange, 
  onApplicationSubmitted, 
  onApplyClick, 
  onSaveJob,
  applicationSubmitted,
  isSaved = false
}: JobDetailDialogProps) {
  const isMobile = useIsMobile();
  const { generateToken, isGenerating } = useSecureReferralToken();
  const { hasRecruiterAccess } = useAccessFlags();
  const { user } = useUser();
  const canGenerateLinks = !!user;
  const toast = useTranslatedToast();
  const { t } = useTranslation('ui');
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [showBountyDialog, setShowBountyDialog] = useState(false);
  const [bountyData, setBountyData] = useState<BountyWorkflowSummary | null>(null);
  const [loadingBounty, setLoadingBounty] = useState(false);
  
  if (!job) return null;

  const salaryText = formatSalary(
    job.salary_min,
    job.salary_max,
    job.currency || 'USD',
    undefined,
    job.salary_period
  );
  const companyName = job.company_profiles?.company_name || 
                      job.company_profiles?.name || 
                      job.company?.name || 
                      'Company';
  const companyLogo = job.company_profiles?.company_logo_url || 
                      job.company_profiles?.logo_url || 
                      job.company?.logo_url;
  const companyIdentifier = job.company_profiles?.company_identifier || 
                           job.company?.company_identifier;

  const handleShareClick = async () => {
    if (!hasRecruiterAccess || !canGenerateLinks) {
      setShowReferralDialog(true);
      return;
    }

    // Fetch bounty data asynchronously
    setLoadingBounty(true);
    try {
      const { data: bountySettings } = await supabase
        .from('company_job_bounty_workflow_settings')
        .select('*')
        .eq('job_id', job.id as any)
        .maybeSingle();

      const summary: BountyWorkflowSummary = (bountySettings as any)?.bounty_enabled
        ? {
            enabled: true,
            settings: bountySettings as any,
            totalPotential: 0,
            totalAwarded: 0,
            totalPaid: 0,
            stageBreakdown: {},
          }
        : {
            enabled: false,
            totalPotential: 0,
            totalAwarded: 0,
            totalPaid: 0,
            stageBreakdown: {},
          };

      setBountyData(summary);
      setShowBountyDialog(true);
    } catch (error) {
      console.error("Error fetching bounty data:", error);
      toast.error('error.generic');
      setBountyData({ enabled: false, totalPotential: 0, totalAwarded: 0, totalPaid: 0, stageBreakdown: {} });
    } finally {
      setLoadingBounty(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <FullscreenDialogContent className="p-0 flex flex-col overflow-hidden">
        <VisuallyHidden>
          <DialogTitle>{job.title}</DialogTitle>
          <DialogDescription>
            View detailed information about the {job.title} position at {companyName}
          </DialogDescription>
        </VisuallyHidden>
        
        {/* Mobile-Optimized Header */}
        <div className="relative bg-gradient-to-br from-orange-500/10 via-amber-400/5 to-sky-400/10 border-b flex-shrink-0">
          <div className="p-4 md:p-5">
            
            {/* Mobile: Stacked layout, Desktop: Grid layout */}
            <div className="flex flex-col md:grid md:grid-cols-[1fr,auto] gap-4">
              
              {/* Content Section */}
              <div className="flex flex-col space-y-3 min-w-0">
                {/* Company & Job Title */}
                <div className="flex items-start gap-3">
                  <Avatar className="h-14 w-14 md:h-16 md:w-16 border-2 border-primary/10 shadow-lg shrink-0">
                    <AvatarImage src={companyLogo} alt={companyName} />
                    <AvatarFallback className="bg-gradient-to-br from-primary/10 to-accent/5">
                      <Building2 className="h-6 w-6 md:h-7 md:w-7 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <HeadingMedium className="text-foreground mb-1 md:mb-1.5 truncate">{companyName}</HeadingMedium>
                    <DisplaySmall className="text-foreground leading-tight line-clamp-2 md:truncate">{job.title}</DisplaySmall>
                  </div>
                  {/* Close button - Mobile only in header */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    className="h-10 w-10 shrink-0 md:hidden"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                {/* Stats Row - Scrollable on mobile */}
                <div className="flex flex-wrap gap-2 overflow-x-auto scrollbar-none">
                  {salaryText && (
                    <Caption className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted shrink-0">
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                      {salaryText}
                    </Caption>
                  )}
                  {job.location && (
                    <Caption className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted shrink-0">
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                      {job.location}
                    </Caption>
                  )}
                  {job.employment_type && (
                    <Caption className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted shrink-0">
                      <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                      {job.employment_type}
                    </Caption>
                  )}
                  <Caption className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted shrink-0 text-muted-foreground">
                    <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                    {format(new Date(job.created_at), 'MMM d, yyyy')}
                  </Caption>
                </div>
              </div>

              {/* Desktop Controls - Hidden on mobile */}
              <div className="hidden md:flex flex-col items-end justify-between gap-2.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOpenChange(false)}
                  className="h-10 w-10 shrink-0"
                >
                  <X className="h-5 w-5" />
                </Button>

                <div className="flex items-center gap-2">
                  {onSaveJob && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onSaveJob(job.id)}
                      className={cn(
                        "h-10 px-4 rounded-lg shrink-0 bg-background/60 backdrop-blur-sm font-medium",
                        isSaved && "text-red-500 hover:text-red-600"
                      )}
                      title={isSaved ? t('saved') : t('save')}
                    >
                      <Heart className={cn("w-4 h-4 mr-2", isSaved && "fill-current")} />
                      {t('save')}
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleShareClick}
                    className="shrink-0 h-10 px-4 rounded-lg bg-background/60 backdrop-blur-sm font-medium"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {hasRecruiterAccess && canGenerateLinks ? t('referral_link') : t('share')}
                  </Button>
                  {onApplyClick && (
                    <Button 
                      variant={applicationSubmitted ? "gradient-soft" : "gradient"}
                      size="sm"
                      onClick={() => onApplyClick(job)}
                      disabled={applicationSubmitted}
                    className="shrink-0 h-10 px-5 rounded-lg font-semibold"
                  >
                    {applicationSubmitted ? t('track_application') : t('apply')}
                  </Button>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Main Content - Scrollable with mobile padding */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 pb-24 md:pb-6">{/* Extra bottom padding for floating action bar */}
            {/* Company Overview */}
            {job.company_profiles?.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    <span>{t('about_company')}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm md:text-base leading-relaxed prose prose-sm md:prose-base max-w-none dark:prose-invert">
                    <MarkdownMessage content={job.company_profiles.description} />
                  </div>
                  {job.company_profiles.industry && (
                    <div className="flex items-center gap-2 text-sm md:text-base">
                      <span className="text-muted-foreground">{t('industry')}</span>
                      <Badge variant="secondary">{job.company_profiles.industry}</Badge>
                    </div>
                  )}
                  {job.company_profiles.website && (
                    <a 
                      href={job.company_profiles.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary hover:underline min-h-[44px] md:min-h-0"
                    >
                      <Globe className="w-4 h-4" />
                      <Caption>{t('visit_website')}</Caption>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('job_description')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MarkdownMessage content={job.description} className="md:prose-base" />
              </CardContent>
            </Card>

            {/* AI Interviewer Badge */}
            {job.has_ai_interviewer && (
              <Card className="border-orange-500/20 bg-orange-500/5">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm md:text-base">{t('ai_powered_interviews')}</p>
                      <Caption className="block">
                        {t('ai_screening_description')}
                      </Caption>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
        </div>

        {/* Mobile Floating Action Bar - positioned within dialog */}
        {isMobile && (
          <div className="sticky bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t p-3 flex gap-2 shrink-0 safe-area-inset-bottom">
            {onSaveJob && (
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => onSaveJob(job.id)}
                className={cn(
                  "flex-1 min-h-[48px] rounded-lg touch-manipulation px-3 font-medium",
                  isSaved && "text-red-500 hover:text-red-600"
                )}
              >
                <Heart className={cn("w-5 h-5", isSaved && "fill-current")} />
                <span className="ml-1.5 text-xs font-medium truncate">{isSaved ? t('saved') : t('save')}</span>
              </Button>
            )}
            <Button 
              variant="outline"
              size="lg"
              onClick={handleShareClick}
              className="flex-1 min-h-[48px] rounded-lg touch-manipulation px-3 font-medium"
            >
              <Share2 className="w-5 h-5" />
              <span className="ml-1.5 text-xs font-medium truncate">{t('share')}</span>
            </Button>
            {onApplyClick && (
              <Button 
                variant={applicationSubmitted ? "gradient-soft" : "gradient"}
                size="lg"
                onClick={() => onApplyClick(job)}
                disabled={applicationSubmitted}
                className="flex-1 min-h-[48px] rounded-lg touch-manipulation px-4 font-semibold"
              >
                <span className="text-xs font-semibold truncate">{applicationSubmitted ? t('track_application') : t('apply')}</span>
              </Button>
            )}
          </div>
        )}

        </FullscreenDialogContent>
      </Dialog>

      <ReferralExplanationDialog 
        open={showReferralDialog} 
        onOpenChange={setShowReferralDialog}
      />

      <ReferralBountyDialog
        open={showBountyDialog}
        onOpenChange={setShowBountyDialog}
        bountySummary={bountyData}
        referralLink=""
        jobId={job.id}
        companyId={job.company_id}
        jobTitle={job.title}
        loading={loadingBounty}
        nested={true}
        autoGenerateLink={true}
      />
    </>
  );
}